import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Observable, takeUntil, withLatestFrom, filter, map } from 'rxjs';
import { NgxsFormPluginModule, UpdateFormValue } from '@ngxs/form-plugin';
import { Select, ofActionSuccessful } from '@ngxs/store';
import { TranslateModule } from '@ngx-translate/core';

import { DataUpdateResponseWithAttachmentUrl, WebApiResponseBase, DropDownItem } from '@app/shared/classes/general';
import { DatepickerNormalFormat, NgxsActionName, NgxsFormPath, SelectItems } from '@app/shared/constants';
import { AngularMaterialModule } from '@app/shared/modules/angular-material.module';
import { AppDataState } from '@app/shared/stores/app-data.state';
import { EditModalDialogBasedComponent } from '@shared/components/edit-modal-dialog/edit-modal-dialog.based.component';
import { SpinnerButtonComponent } from '@shared/components/spinner-button/spinner-button.component';
import * as AppDataAction from '@app/shared/actions/app-data.action';
import { Coaching } from '@app/shared/classes/coaching';
import { InjectDialogData } from '@shared/components/edit-modal-dialog/edit-modal-dialog.constant';
import { LogicalOperator, Status } from '@app/shared/enums';
import { Driver } from '@shared/classes/driver';
import { DriverSearchGridComponent } from '@app/shared/components/driver-search-grid/driver-search-grid.component';
import { UserProfileSearchGridComponent } from '@shared/components/user-profile-search-grid/user-profile-search-grid.component';
import { UserProfile } from '@app/shared/classes/user-profile';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { CommonDataState } from '@app/shared/stores/common-data.state';
import { Terminal } from '@app/shared/classes/terminal';

@Component({
  selector: 'app-coaching-edit-modal-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, SpinnerButtonComponent, TranslateModule, NgxsFormPluginModule, ReactiveFormsModule, MatDialogModule, AngularMaterialModule, DriverSearchGridComponent, UserProfileSearchGridComponent],
  templateUrl: './coaching-edit-modal-dialog.component.html',
  styleUrl: './coaching-edit-modal-dialog.component.css',
  providers: [
    DatePipe,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: DatepickerNormalFormat },
  ],
})
export class CoachingEditModalDialogComponent extends EditModalDialogBasedComponent<CoachingEditModalDialogComponent, Coaching> {
  formPath = NgxsFormPath.COACHING_EDIT_FORM;
  statusSelectItems = SelectItems.statusSelectItems;
  title = "";
  is3pl = false;

  driverList$: Observable<Driver[]>;
  mentorNameFilterSub = new BehaviorSubject<string>('');
  mentorNameFilter$ = this.mentorNameFilterSub.asObservable();
  driverApexIDOrNameFilterSub = new BehaviorSubject<string>('');
  driverApexIDOrNameFilter$ = this.driverApexIDOrNameFilterSub.asObservable();

  terminalSelectItems$: Observable<DropDownItem[]>;

  @Select(CommonDataState.profileMe) profileMe$: Observable<UserProfile>;
  @Select(AppDataState.tasksExecuting([NgxsActionName.UPDATE_COACHING, NgxsActionName.UPLOAD_ATTACHMENT], LogicalOperator.OR)) processing$: Observable<boolean>;
  @Select(AppDataState.taskError(NgxsActionName.UPDATE_COACHING)) errorResponse$: Observable<WebApiResponseBase>;
  @Select(AppDataState.taskResponse(NgxsActionName.UPDATE_COACHING)) successResponse$: Observable<DataUpdateResponseWithAttachmentUrl>;

  @Select(AppDataState.taskResponse(NgxsActionName.GET_DRIVER_RELATED_TERMINAL_LIST)) terminals$: Observable<Terminal[]>;

  constructor(public dialogRef: MatDialogRef<CoachingEditModalDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: InjectDialogData<Coaching>) {
    super();
    this.dataInit(dialogRef, data);
    this.isCreate = data.isCreate;
    this.initForm(data);
    this.title = data.isCreate ? "ml_144036" : "ml_143925";
  }

  initForm = (data: InjectDialogData<Coaching>) => {
    this.profileMe$.pipe(takeUntil(this.destroy$)).subscribe(profileMe => {
      if (data.isCreate) {
        this.editForm = this.formBuilder.group({
          coaching_Id: -1,
          driver_Id: -1,
          driverApexId: ["", Validators.required],
          driverName: ["", Validators.required],
          coachingtime: ["", Validators.required],
          abstract: ["", Validators.required],
          coachingResult: ["", Validators.required],
          mentorId: [profileMe.apexId, Validators.required],
          mentorName: [profileMe.fullName, Validators.required],
          emIncidentNumber: "",
          attachment: ["", Validators.required],
          attachmentVisibletoDriver: [false, Validators.required],
          driver3pl: "",
          status: Status.ACTIVE,
          driver3PLterminal_Id: -1,
          driver3PLflag: false,
        });
      }
      else {
        this.editForm = this.formBuilder.group({
          coaching_Id: data.data.coaching_Id,
          driver_Id: data.data.driver_Id,
          driverApexId: [data.data.driverApexId, Validators.required],
          driverName: [data.data.driverName, Validators.required],
          coachingtime: [data.data.coachingTime, Validators.required],
          abstract: [data.data.title, Validators.required],
          coachingResult: [data.data.coachingResult, Validators.required],
          mentorId: [data.data.mentorId, Validators.required],
          mentorName: [data.data.mentorName, Validators.required],
          emIncidentNumber: data.data.emIncidentNumber,
          attachment: [data.data.attachment, Validators.required],
          attachmentVisibletoDriver: [data.data.attachmentVisibletoDriver, Validators.required],
          driver3pl: data.data.driver3PL,
          status: data.data.status,
          driver3PLterminal_Id: data.data.driver3PLterminal_Id,
          driver3PLflag: data.data.driver3PLflag,
        });
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.terminalSelectItems$ = this.terminals$.pipe(filter(Boolean), withLatestFrom(this.profileMe$),
      map(([terminals, profileMe]) => terminals.filter(terminal => terminal.country === profileMe.country)
        .map(terminal => {
          return {
            value: terminal.terminal_Id,
            label: `${terminal.terminalId} ${terminal.terminalName}`
          } as DropDownItem;
        })
      )
    );    

    this.editForm.get('driver3PLflag').valueChanges.subscribe(val => {
      this.is3pl = val === true;
      if (this.is3pl) {
        this.editForm.controls['driver3PLterminal_Id'].setValidators([Validators.required]);
        this.editForm.controls['driver3pl'].setValidators([Validators.required]);
        this.editForm.controls['driverApexId'].clearValidators();
        this.editForm.controls['driverName'].clearValidators();

        this.editForm.controls['driverName'].setValue('');
        this.editForm.controls['driverApexId'].setValue('');
        this.editForm.controls['driver_Id'].setValue(-1);
      } else {
        this.editForm.controls['driver3PLterminal_Id'].clearValidators();
        this.editForm.controls['driver3pl'].clearValidators();
        this.editForm.controls['driverApexId'].setValidators([Validators.required]);
        this.editForm.controls['driverName'].setValidators([Validators.required]);

        this.editForm.controls['driver3PLterminal_Id'].setValue(-1);        
        this.editForm.controls['driver3pl'].setValue('');
      }
      this.editForm.controls['driver3PLterminal_Id'].updateValueAndValidity();
      this.editForm.controls['driver3pl'].updateValueAndValidity();
      this.editForm.controls['driverApexId'].updateValueAndValidity();
      this.editForm.controls['driverName'].updateValueAndValidity();
    });

    this.actions$.pipe(ofActionSuccessful(AppDataAction.UpdateCoaching), withLatestFrom(this.successResponse$, this.errorResponse$), takeUntil(this.destroy$)).subscribe(
      ([_, successResponse, errorResponse]) => this.processAfterUpdated(successResponse, errorResponse));
  }

  onMentorNameKeyUp = (event: any) => {
    this.mentorNameFilterSub.next(event.target.value as string);
  }

  onMentorItemClicked = (event: any) => {
    var currentValue = { mentorId: "", mentorName: '' };
    if (event.toggled) {
      var userProfile = event.item as UserProfile;
      currentValue.mentorId = userProfile.apexId;
      currentValue.mentorName = userProfile.fullName;
      this.mentorNameFilterSub.next("");
    }

    this.store.dispatch(new UpdateFormValue({
      value: currentValue,
      path: this.formPath
    }));
  }

  onDriverNameKeyUp = (event: any) => {
    this.driverApexIDOrNameFilterSub.next(event.target.value as string);
  }

  onDriverItemClicked = (event: any) => {
    var currentValue = { driver_Id: -1, driverApexId: '', driverName: '' };
    if (event.toggled) {
      var driver = event.item as Driver;
      currentValue.driver_Id = driver.driver_Id;
      currentValue.driverApexId = driver.apexId;
      currentValue.driverName = driver.fullName;
      this.driverApexIDOrNameFilterSub.next("");
    }

    this.store.dispatch(new UpdateFormValue({
      value: currentValue,
      path: this.formPath
    }));
  }

  onOkClick = () => {
    this.store.dispatch(new AppDataAction.UpdateCoaching());
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.driverApexIDOrNameFilterSub.complete();
    this.mentorNameFilterSub.complete();
  }
}
